import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, Loader2, UploadCloud, RefreshCw, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { CreatableSelect } from "./CreatableSelect";
import { DropLocationField } from "./DropLocationField";
import { TransferItemsTable } from "./TransferItemsTable";
import { InvoiceFileLink } from "./InvoiceFileLink";
import { deriveStatus, errMsg, fmtQty, lineTotals, STATUS_STYLES } from "./mcfUtils";
import { SearchableSelect } from "@/components/ui/searchable-select";

const ACCEPT = "application/pdf,image/jpeg,image/png,image/webp";

const emptyHeader = { invoice_number: "", invoice_date: "", pickup_date: "", status: "In-Transit", pickup_location: "", pickup_warehouse_id: "", drop_warehouse_id: "", courier_partner: "" };

function UploadZone({ onFile, busy, label = "Upload stock transfer invoice" }) {
  const ref = useRef(null);
  return (
    <div
      onClick={() => !busy && ref.current?.click()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); if (!busy && e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0]); }}
      className="cursor-pointer rounded-xl border-2 border-dashed border-teal-300 bg-teal-50/40 hover:bg-teal-50 transition-colors p-10 text-center"
      data-testid="mcf-invoice-dropzone"
    >
      <input ref={ref} type="file" accept={ACCEPT} className="hidden" onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} data-testid="mcf-invoice-file-input" />
      {busy ? (
        <div className="flex flex-col items-center gap-2 text-[#1F8F88]">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="font-medium">Reading invoice…</p>
          <p className="text-xs text-muted-foreground">Extracting invoice number, date and SKU lines</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <UploadCloud className="h-8 w-8 text-[#1F8F88]" />
          <p className="font-medium">{label}</p>
          <p className="text-xs text-muted-foreground">PDF, JPG, PNG or WEBP · up to 15 MB</p>
        </div>
      )}
    </div>
  );
}

export function TransferFormDialog({ open, onClose, transfer, lookups, onSaved, isAdmin }) {
  const [header, setHeader] = useState(emptyHeader);
  const [items, setItems] = useState([]);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const pickupTouched = useRef(false);
  const requestId = useRef("");
  const isEdit = !!transfer;

  useEffect(() => {
    if (!open) return;
    requestId.current = crypto.randomUUID();
    pickupTouched.current = isEdit;
    setWarnings([]);
    if (transfer) {
      setHeader({ ...emptyHeader, ...Object.fromEntries(Object.keys(emptyHeader).map((k) => [k, transfer[k] ?? emptyHeader[k]])) });
      setItems(transfer.items.map((i) => ({ ...i, matched: true })));
      setInvoiceFile(transfer.invoice_file || null);
    } else {
      const hub = lookups.pickupWarehouses.find((w) => w.warehouse_type === "weekly_allocation");
      setHeader({ ...emptyHeader, pickup_warehouse_id: hub?.id || "" });
      setItems([]);
      setInvoiceFile(null);
    }
  }, [open, transfer, isEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || isEdit || header.pickup_warehouse_id) return;
    const hub = lookups.pickupWarehouses.find((w) => w.warehouse_type === "weekly_allocation");
    if (hub) setHeader((h) => ({ ...h, pickup_warehouse_id: hub.id }));
  }, [open, isEdit, lookups.pickupWarehouses, header.pickup_warehouse_id]);

  const set = (k, v) => setHeader((h) => {
    const next = { ...h, [k]: v };
    if (k === "invoice_date" && !pickupTouched.current) next.pickup_date = v;
    return next;
  });

  const handleFile = async (file, replaceOnly = false) => {
    setExtracting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/mcf/extract`, fd);
      setInvoiceFile(res.data.invoice_file);
      if (replaceOnly) {
        toast.success("Invoice file replaced. Save to keep the change.");
        return;
      }
      const ex = res.data.extracted;
      setHeader((h) => ({ ...h, invoice_number: ex.invoice_number || "", invoice_date: ex.invoice_date || "", pickup_date: ex.invoice_date || "" }));
      setItems(ex.items.map((i) => ({ ...i, sent_qty: i.sent_qty ?? "" })));
      setWarnings(res.data.warnings || []);
    } catch (err) {
      toast.error(errMsg(err, "Invoice upload failed"));
    } finally {
      setExtracting(false);
    }
  };

  const clientErrors = () => {
    const errs = [];
    if (!header.invoice_number.trim()) errs.push("Invoice Number is required");
    if (!header.invoice_date) errs.push("Invoice Date is required");
    if (!header.pickup_warehouse_id && !header.pickup_location) errs.push("Pickup Location is required");
    if (!header.drop_warehouse_id) errs.push("Drop Location is required");
    if (header.pickup_warehouse_id && header.pickup_warehouse_id === header.drop_warehouse_id) errs.push("Pickup and Drop Location cannot be the same warehouse");
    const drop = lookups.dropWarehouses.find((w) => w.id === header.drop_warehouse_id);
    if (drop && (!drop.city?.trim() || !drop.state?.trim())) errs.push("Drop Location is missing City/State in Warehouse Master");
    if (!items.length) errs.push("At least one SKU line is required");
    items.forEach((i, n) => {
      if (!String(i.sku_id || "").trim() || !String(i.sku_name || "").trim()) errs.push(`Line ${n + 1}: select or enter the SKU`);
      if (!(Number(i.sent_qty) > 0)) errs.push(`Line ${n + 1}: Sent Qty must be > 0`);
      if (Number(i.grn_qty) < 0) errs.push(`Line ${n + 1}: GRN Qty cannot be negative`);
      if (Number(i.grn_qty) > Number(i.sent_qty) && !(isAdmin && i.discrepancy_approved)) errs.push(`Line ${n + 1}: GRN exceeds Sent Qty`);
    });
    return errs;
  };

  const submit = async (confirmPending = false) => {
    const errs = clientErrors();
    if (errs.length) {
      toast.error(errs[0], { description: errs.length > 1 ? `+${errs.length - 1} more issue(s)` : undefined });
      return;
    }
    setSaving(true);
    const payload = {
      ...header,
      client_request_id: isEdit ? undefined : requestId.current,
      invoice_file: invoiceFile,
      confirm_pending_delivered: confirmPending,
      items: items.map((i) => ({ sku_id: i.sku_id, sku_name: i.sku_name, item_code: i.item_code || "", sent_qty: Number(i.sent_qty), grn_qty: Number(i.grn_qty) || 0, discrepancy_approved: !!i.discrepancy_approved })),
    };
    try {
      if (isEdit) await axios.put(`${API}/mcf/transfers/${transfer.id}`, payload);
      else await axios.post(`${API}/mcf/transfers`, payload);
      toast.success(isEdit ? "Stock transfer updated" : "Stock transfer saved");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err, "Could not save stock transfer"));
    } finally {
      setSaving(false);
    }
  };

  const receiveAll = () => {
    setItems((arr) => arr.map((i) => ({ ...i, grn_qty: i.sent_qty, discrepancy_approved: false })));
    toast.success("GRN filled with Sent qty — status will be Delivered. Review and save.");
  };

  const t = lineTotals(items);
  const showForm = isEdit || invoiceFile;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !saving && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="mcf-transfer-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit Stock Transfer · ${transfer.invoice_number}` : "Add Stock Transfer"}</DialogTitle>
        </DialogHeader>

        {!showForm && <UploadZone onFile={(f) => handleFile(f)} busy={extracting} />}

        {showForm && (
          <div className="space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-2 rounded-lg bg-slate-50 px-3 py-2">
              <InvoiceFileLink file={invoiceFile} testId="mcf-form-invoice-file" />
              <label className="text-xs text-[#1F8F88] font-medium inline-flex items-center cursor-pointer hover:underline">
                {extracting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                Replace invoice
                <input type="file" accept={ACCEPT} className="hidden" onChange={(e) => e.target.files[0] && handleFile(e.target.files[0], isEdit)} data-testid="mcf-replace-invoice-input" />
              </label>
            </div>

            {warnings.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 space-y-1" data-testid="mcf-extraction-warnings">
                {warnings.map((w) => <p key={w} className="flex gap-2"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />{w}</p>)}
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Invoice No. *</Label>
                <Input value={header.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} data-testid="mcf-invoice-number-input" />
              </div>
              <div className="space-y-2">
                <Label>Invoice Date *</Label>
                <Input type="date" value={header.invoice_date} onChange={(e) => set("invoice_date", e.target.value)} data-testid="mcf-invoice-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Pickup Date</Label>
                <Input type="date" value={header.pickup_date} onChange={(e) => { pickupTouched.current = true; set("pickup_date", e.target.value); }} data-testid="mcf-pickup-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Pickup Location *</Label>
                <SearchableSelect
                  options={[
                    ...lookups.pickupWarehouses.map((w) => ({ value: w.id, label: `${w.warehouse_name} (${w.warehouse_code})`, subtitle: w.address || "No address in Warehouse Master", searchTerms: [w.warehouse_code] })),
                    ...(header.pickup_warehouse_id && !lookups.pickupWarehouses.some((w) => w.id === header.pickup_warehouse_id) ? [{ value: header.pickup_warehouse_id, label: `${header.pickup_location} (inactive)` }] : []),
                  ]}
                  value={header.pickup_warehouse_id}
                  onValueChange={(v) => set("pickup_warehouse_id", v)}
                  placeholder={header.pickup_location || "Select pickup warehouse"}
                  data-testid="mcf-pickup-location-select"
                />
                <p className="text-xs text-muted-foreground" data-testid="mcf-pickup-address">
                  {lookups.pickupWarehouses.find((w) => w.id === header.pickup_warehouse_id)?.address || (header.pickup_warehouse_id ? transfer?.pickup_address : "") || (!header.pickup_warehouse_id && header.pickup_location ? `Saved: ${header.pickup_location}` : "")}
                </p>
              </div>
              <DropLocationField warehouses={lookups.dropWarehouses} value={header.drop_warehouse_id} onChange={(v) => set("drop_warehouse_id", v)} onWarehousesChanged={lookups.reloadWarehouses} isAdmin={isAdmin} />
              <div className="space-y-2">
                <Label>Drop City / State</Label>
                {(() => {
                  const w = lookups.dropWarehouses.find((x) => x.id === header.drop_warehouse_id);
                  return <Input readOnly value={w ? [w.city, w.state].filter(Boolean).join(", ") : ""} placeholder="Auto-filled from MCF Code" className="bg-slate-50" data-testid="mcf-drop-city-state" />;
                })()}
              </div>
              <div className="space-y-2">
                <Label>Courier Partner</Label>
                <CreatableSelect options={lookups.couriers} value={header.courier_partner} onChange={(v) => set("courier_partner", v)} onCreate={lookups.addCourier} placeholder="Select courier partner" testId="mcf-courier-select" />
              </div>
              <div className="space-y-2">
                <Label>Status <span className="text-xs font-normal text-muted-foreground">(follows GRN)</span></Label>
                <div className="h-10 flex items-center" data-testid="mcf-status-derived">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[deriveStatus(items)]}`}>{deriveStatus(items)}</span>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="font-semibold">SKU-wise Sent & GRN</h3>
                <div className="flex items-center gap-3">
                  <p className="text-xs text-muted-foreground">Sent {fmtQty(t.sent)} · GRN {fmtQty(t.grn)} · Pending {fmtQty(t.pending)} · Excess {fmtQty(t.excess)}</p>
                  {isAdmin && <Button size="sm" variant="outline" onClick={receiveAll} disabled={!items.length || items.some((i) => !(Number(i.sent_qty) > 0))} className="h-8 border-[#1F8F88] text-[#1F8F88] hover:bg-teal-50 hover:text-[#1F8F88]" title="Set GRN = Sent qty for every SKU" data-testid="mcf-receive-stock-button">
                    <PackageCheck className="h-4 w-4 mr-1" /> Receive Stock
                  </Button>}
                </div>
              </div>
              <TransferItemsTable items={items} setItems={setItems} products={lookups.products} isAdmin={isAdmin} />
              {!isAdmin && <p className="text-xs text-muted-foreground mt-1">GRN quantities can be updated by an admin.</p>}
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={onClose} disabled={saving} data-testid="mcf-form-cancel">Cancel</Button>
              <Button onClick={() => submit(false)} disabled={saving || extracting} className="bg-[#1F8F88] hover:bg-[#197a74] text-white" data-testid="mcf-form-save">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {isEdit ? "Save Changes" : "Save Stock Transfer"}
              </Button>
            </div>
          </div>
        )}

      </DialogContent>
    </Dialog>
  );
}
